import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@/features/auth'
import { registerVipInterest, checkVipRegistration } from '@/services/vipService'
import { VipDashboard } from '@/components/vip/VipDashboard'

/* ── Easy-to-edit copy ─────────────────────────────────────────── */
const COPY = {
  login: {
    icon: '\u2709\uFE0F',
    heading: 'You are currently not logged in',
    description:
      'Please return to this page after logging in to begin.',
    button: 'Log In',
  },
  landing: {
    icon: '\u2709\uFE0F',
    heading: "You've Been Invited",
    description:
      "You've been personally invited to join an exclusive VIP experience.",
    button: 'Register My Account',
    footer: 'By invitation only',
  },
} as const

/* ── Component ─────────────────────────────────────────────────── */
export function VipPage() {
  const navigate = useNavigate()
  const { isAuthenticated, isLoading: authLoading } = useAuth()
  const [step, setStep] = useState<'landing' | 'dashboard'>('landing')
  const [loading, setLoading] = useState(true)

  // Check if already registered (only when logged in)
  useEffect(() => {
    if (authLoading) return
    if (!isAuthenticated) {
      setLoading(false)
      return
    }

    checkVipRegistration()
      .then((result) => {
        if (result.registered) {
          setStep('dashboard')
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [authLoading, isAuthenticated])

  const handleRegister = () => {
    setStep('dashboard')
    registerVipInterest().catch(() => {})
  }

  // Loading spinner
  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-[#ece6dd] flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#6b7850]" />
      </div>
    )
  }

  // Not logged in — ask them to log in
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#ece6dd] flex items-center justify-center px-4">
        <div className="bg-white/80 rounded-3xl shadow-lg p-8 max-w-sm w-full text-center">
          <div className="text-4xl mb-4">{COPY.login.icon}</div>
          <h1 className="text-2xl font-bold text-gray-700 mb-2">
            {COPY.login.heading}
          </h1>
          <p className="text-sm text-gray-400 mb-8 leading-relaxed">
            {COPY.login.description}
          </p>
          <button
            onClick={() => navigate('/login?returnTo=/vip')}
            className="w-full py-3 bg-[#6b7850] text-white font-semibold rounded-xl hover:bg-[#5c5b44] transition-colors"
          >
            {COPY.login.button}
          </button>
        </div>
      </div>
    )
  }

  // Logged in — landing or dashboard
  return (
    <div className="min-h-screen bg-[#ece6dd] flex flex-col items-center px-4 py-8">
      {step === 'landing' ? (
        <div className="bg-white/80 rounded-3xl shadow-lg p-8 max-w-sm w-full text-center mt-12">
          <div className="text-4xl mb-4">{COPY.landing.icon}</div>
          <h1 className="text-2xl font-bold text-gray-700 mb-2">
            {COPY.landing.heading}
          </h1>
          <p className="text-sm text-gray-400 mb-8 leading-relaxed">
            {COPY.landing.description}
          </p>
          <button
            onClick={handleRegister}
            className="w-full py-3 bg-[#6b7850] text-white font-semibold rounded-xl hover:bg-[#5c5b44] transition-colors"
          >
            {COPY.landing.button}
          </button>
          <p className="text-xs text-gray-400 mt-4">
            {COPY.landing.footer}
          </p>
        </div>
      ) : (
        <div className="w-full max-w-sm">
          <VipDashboard />

          <div className="mt-5 text-center">
            <button
              onClick={() => navigate('/welcome')}
              className="text-sm text-white/70 hover:text-white font-medium transition-colors cursor-pointer"
            >
              Back to Home
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
